// Per-block rendering. Per spec/tech-arch.md §3.2 + §11.4 + §11.5 + §11.6
// + spec/build-plan.md P-018 row.
//
// **Defensive validation at the render seam.** Tech Arch §11.6 step 6
// reads "the renderer assumes already-validated input" but the Build-Plan
// row's QA item requires structured location info on schema-validation
// failure. Reconciled per OC §0 (quality + bus-factor over speed) in
// favor of defense-in-depth: parse `options` and `inputs` against the
// per-Block Zod schemas at this seam. The Email schema (Tech Arch §2.5)
// types both as `z.unknown()` because shape varies per blockId; this
// seam is where the per-Block narrowing happens. Trust-boundary surface
// covered: form generator (P-025+) bugs, future "JSON-import-then-edit-
// then-render" drift, P-021 schema migrations producing stale cached
// shapes. Cost is one Zod parse per Block per render, amortized by the
// 50 ms preview debounce. See D-077.
//
// **`prepareBlock` factor (D-079).** P-019's renderEmail needs the parsed
// inputs to evaluate `autoHide` for its backward-pass `followingBlocks-
// AllHidden` lookahead. To avoid double-parsing per block, the
// lookup/skip/parse/autoHide pipeline lives in `prepareBlock`, which
// returns a discriminated `{kind:'skip'} | {kind:'render', def, options,
// inputs}` result. `renderBlock` calls `prepareBlock` then either
// returns EMPTY_HTML or invokes `def.render(...)`. renderEmail walks
// once with `prepareBlock`, computes `followingBlocksAllHidden`, then
// calls `def.render` directly with the cached parsed values.
//
// **`parseOrThrow` lib helper.** Hoisted to `src/lib/parse-or-throw.ts`
// at the 3rd consumer (P-034 JSON import) per the project's 3-use
// threshold (D-067 / D-072 / D-077 / D-094). The render seam supplies
// a per-field context prefix that flows into the thrown Error message.

import type { z } from 'zod'

import { parseOrThrow } from '../lib/parse-or-throw'
import { blocks } from '../registry/blocks'
import type { EmailBlockInstance } from '../store/schemas'
import { EMPTY_HTML, HtmlString, type BlockDefinition, type RenderContext } from '../types'

// Widened registry view for the lookup site. The registry's
// `as const satisfies Record<string, BlockDefinition<any, any>>` shape
// (D-075) preserves per-Block narrowing on `typeof blocks`, but indexing
// by a runtime string returns the union of all 15 concrete BlockDefinition
// types — which TS treats as a function whose options/inputs argument is
// the *intersection* of all 15 schemas (impossible to satisfy). Widening
// to `BlockDefinition<z.ZodTypeAny, z.ZodTypeAny>` collapses the union
// into a single function whose options/inputs are `any`, matching what
// renderBlock actually needs (it parses `unknown` against the schemas
// and threads the parsed values through). Same `<any, any>`-equivalent
// relaxation as D-075 + a single eslint-disable for the cast itself.
const blockRegistry = blocks as unknown as Record<
  string,
  BlockDefinition<z.ZodTypeAny, z.ZodTypeAny> | undefined
>

/**
 * Result of preparing a block: either it's skipped (included=false,
 * autoHide, or unknown blockId is handled via throw upstream) or it's
 * ready to render with already-parsed options/inputs and the looked-up
 * BlockDefinition. renderEmail (P-019) consumes this to compute
 * `followingBlocksAllHidden` without re-parsing in a second pass.
 */
export type PreparedBlock =
  | { kind: 'skip' }
  | {
      kind: 'render'
      def: BlockDefinition<z.ZodTypeAny, z.ZodTypeAny>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      options: any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      inputs: any
    }

function parseField(
  schema: z.ZodTypeAny,
  value: unknown,
  blockId: string,
  instanceId: string,
  field: 'options' | 'inputs',
): unknown {
  return parseOrThrow(
    schema,
    value,
    `renderBlock: blockId="${blockId}" instanceId="${instanceId}" — ${field} validation failed`,
  )
}

export function prepareBlock(instance: EmailBlockInstance): PreparedBlock {
  const { blockId, instanceId } = instance

  const def = blockRegistry[blockId]
  if (def === undefined) {
    throw new Error(
      `renderBlock: blockId="${blockId}" instanceId="${instanceId}" — unknown blockId (not in registry)`,
    )
  }

  if (instance.included === false) return { kind: 'skip' }

  const options = parseField(def.optionsSchema, instance.options, blockId, instanceId, 'options')
  const inputs = parseField(def.inputsSchema, instance.inputs, blockId, instanceId, 'inputs')

  if (def.autoHide?.({ inputs }) === true) return { kind: 'skip' }

  return { kind: 'render', def, options, inputs }
}

export function renderBlock(instance: EmailBlockInstance, ctx: RenderContext): HtmlString {
  const prepared = prepareBlock(instance)
  if (prepared.kind === 'skip') return EMPTY_HTML
  return prepared.def.render({ options: prepared.options, inputs: prepared.inputs }, ctx)
}
